import {memo, useState} from 'react';

import styled from 'styled-components';

import {PriceLabel} from '~/shared/components/PriceLabels';
import {EMPTY_ARRAY} from '~/shared/consts/commonConsts';
import {useIsMinDesktop} from '~/shared/hooks/deviceInfo';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexCenterVertically, FlexCenterVertically, flexColumn} from '~/shared/theme/FlexLayout';
import {Order} from '~/shared/services/apiService/models';
import {Title} from '~/shared/components/UserTransactions/shared';

import ChildBodyTable from './ChildBodyTable';
import {Cell, ExpandableContent, MiddleCell, StyledRow, StyledTableHead} from './common';
import ParentHeadRow from './ParentHeadRow';
import ScreenSizeError from './ScreenSizeError';
import {getOrdersGroupedByDate, GroupParentType} from './utils';

const Root = styled.div`
  ${flexColumn}
`;

const FooterRow = styled(StyledRow)`
  height: 40px;
  ${flexCenterVertically};
  background: ${({theme}) => theme.userReport.totalsTable.markedRowBackground};
  color: ${({theme}) => theme.userReport.totalsTable.text};
  border-radius: 8px;
  font-weight: bold;
  padding: 12px 34px;
`;

interface IGroupByDayTable {
  orders: Order[];
  total: number | undefined;
  menuEvents: any;
  reportSettings: Record<string, any>;
  debitTransfersSentRequests: Record<string, any>[];
}

const GroupByDayTable = ({orders, total, menuEvents, reportSettings, debitTransfersSentRequests}: IGroupByDayTable) => {
  const {t} = getLocalizationService();
  const isMinDesktop = useIsMinDesktop();
  
  const [openRowIds, setOpenRowIds] = useState<GroupParentType['parentId'][]>([]);

  const list =
    getOrdersGroupedByDate(orders, {showTransferDebit: reportSettings.showTransferDebit, debitTransfersSentRequests}) ||
    EMPTY_ARRAY;

  const handleParentClick = (id: GroupParentType['parentId']) => {
    if (openRowIds?.includes(id)) {
      setOpenRowIds(openRowIds.filter(_id => _id !== id));
      return;
    }

    setOpenRowIds([...openRowIds, id]);
  };

  if (!isMinDesktop) {
    return <ScreenSizeError />;
  }

  return (
    <Root>
      <Title tabIndex={0} as="h4">
        {t('transaction_report')}
      </Title>
      <StyledTableHead>
        <Cell>{t('date')}</Cell>
        <FlexCenterVertically>
          <Cell width={60}>{t('employee_part')}</Cell>
          <MiddleCell width={70}>{t('employer_part')}</MiddleCell>
          <Cell width={70}>{t('sum')}</Cell>
        </FlexCenterVertically>
      </StyledTableHead>
      {list?.map((parent, index) => {
        const isRowOpen = openRowIds.includes(parent.parentId);
        return (
          <ExpandableContent key={parent.parentId} noBorder={index === list.length - 1}>
            <ParentHeadRow item={parent} isOpen={isRowOpen} onParentClick={handleParentClick} />
            {isRowOpen && <ChildBodyTable items={parent.items} menuEvents={menuEvents} />}
          </ExpandableContent>
        );
      })}

      <FooterRow>
        <Cell width={120}>{t('total_charges')}</Cell>
        <Cell>
          <PriceLabel bold price={total || 0} />
        </Cell>
      </FooterRow>
    </Root>
  );
};

export default memo(GroupByDayTable);
